import React, { memo, useMemo, useState } from "react";

import ChoiceCard from "../components/ChoiceCard.jsx";
import LoadingOrEmpty from "../components/LoadingOrEmpty.jsx";
import SearchBar from "../components/SearchBar.jsx";
import { getCardImage } from "../characterCreatorUtils.js";

function make5eToolsUrl(name) {
  const slug = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "%20");
  return `https://5e.tools/classes.html#${slug}_xphb`;
}

const ClassCard = memo(function ClassCard({ charClass, selected, onSelect }) {
  const subclassCount = charClass.subclasses ? charClass.subclasses.length : 0;
  return (
    <ChoiceCard
      selected={selected}
      imageUrl={getCardImage("classe", charClass)}
      imageLabel={`Classe ${charClass.name}`}
      imageVertical={true}
      badge={charClass.homebrew ? <span className="badge-hw">Homebrew</span> : null}
      onClick={() => onSelect(charClass)}
    >
      <div className="card-name">
        {charClass.name}
        {subclassCount > 0 && (
          <span className="card-subcount" title={`${subclassCount} subclasses disponíveis`}> ({subclassCount})</span>
        )}
      </div>
      <div className="card-traits">
        <span className="trait-tag trait-tag--hp" title="Dado de HP">❤ d{charClass.hit_die}</span>
      </div>
    </ChoiceCard>
  );
});

function ClassDetail({ charClass }) {
  if (!charClass) return null;

  const subclassCount = charClass.subclasses ? charClass.subclasses.length : 0;
  const url = make5eToolsUrl(charClass.name);

  return (
    <div className="side-detail-panel">
      <div className="side-detail-name">{charClass.name}</div>
      {charClass.homebrew && <span className="badge-hw" style={{ position: "static", marginBottom: "0.5rem", display: "inline-block" }}>Homebrew</span>}
      {charClass.detail && <p className="side-detail-summary">{charClass.detail}</p>}

      <div className="side-detail-section">
        <div className="side-detail-row">
          <span className="side-detail-label">Hit Die</span>
          <span className="side-detail-value">d{charClass.hit_die}</span>
        </div>
        {subclassCount > 0 && (
          <div className="side-detail-row">
            <span className="side-detail-label">Subclasses</span>
            <span className="side-detail-value">{subclassCount} disponíveis</span>
          </div>
        )}
      </div>

      {subclassCount > 0 && (
        <div className="side-detail-section">
          <div className="side-detail-section-title">Subclasses</div>
          <ul className="side-detail-list side-detail-list--compact">
            {charClass.subclasses.map((sc) => (
              <li key={sc.name}>{sc.name}</li>
            ))}
          </ul>
        </div>
      )}

      {!charClass.homebrew && (
        <a
          className="side-detail-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          📖 Ver detalhes completos no 5e.tools
        </a>
      )}
    </div>
  );
}

function ClassEmpty() {
  return (
    <div className="side-empty-panel">
      <div className="side-empty-icon">⚔️</div>
      <div className="side-empty-text">
        Selecione uma classe para ver o hit die, subclasses disponíveis e acessar os detalhes completos.
      </div>
    </div>
  );
}

export default function ClassStep({ state, classes, onSelectClass }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");

  const categories = useMemo(() => {
    if (!classes) return [];
    return [...new Set(classes.map((c) => c.category).filter((c) => c && c !== "Homebrew"))];
  }, [classes]);

  const filtered = useMemo(() => {
    if (classes === null) return null;
    let result = classes;
    if (category) result = result.filter((c) => c.category === category);
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.detail?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [classes, query, category]);

  const selectedClass = state.charClass || null;

  return (
    <div className="step-split-layout">
      <div className="step-cards-col">
        <SearchBar value={query} onChange={setQuery} placeholder="Buscar classe…" />
        {categories.length > 0 && (
          <div className="category-filter">
            <button
              type="button"
              className={`cat-btn${!category ? " cat-btn--active" : ""}`}
              onClick={() => setCategory("")}
            >
              Todos
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`cat-btn${category === cat ? " cat-btn--active" : ""}`}
                onClick={() => setCategory(category === cat ? "" : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
        <div className="card-grid card-grid--portrait" id="allClasses">
          <LoadingOrEmpty
            items={filtered}
            loadingText="Carregando classes…"
            emptyText={query ? `Nenhuma classe encontrada para "${query}".` : "Nenhuma classe disponível."}
          >
            {filtered?.map((charClass) => (
              <ClassCard
                key={charClass.name}
                charClass={charClass}
                selected={state.charClass?.name === charClass.name}
                onSelect={onSelectClass}
              />
            ))}
          </LoadingOrEmpty>
        </div>
      </div>

      <div className="step-info-col">
        {selectedClass ? <ClassDetail charClass={selectedClass} /> : <ClassEmpty />}
      </div>
    </div>
  );
}
